import { useMemo, useState, useCallback, useRef, type PointerEvent as RPointerEvent } from 'react';
import { WidgetFrame } from './WidgetFrame';
import { useDashboard } from './useDashboard';
import { getWidget } from './widgets/widgetRegistry';
import { useTheme } from '../../contexts/useTheme';
import { SNAP_GRID, CANVAS_PADDING, MIN_WIDGET_GAP } from './gridConstants';
import type { DashboardLayoutItem } from './widgets/types';

const DEFAULT_MIN_W = 160;
const DEFAULT_MIN_H = 120;

function snap(v: number): number {
  return Math.round(v / SNAP_GRID) * SNAP_GRID;
}

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface DragState {
  id: string;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
}

interface ResizeState {
  id: string;
  edge: ResizeEdge;
  startPointerX: number;
  startPointerY: number;
  orig: { x: number; y: number; width: number; height: number };
  minW: number;
  minH: number;
}

const HANDLE_SIZE = 8;

const EDGE_CURSORS: Record<ResizeEdge, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
  sw: 'nesw-resize',
};

function ResizeHandles({
  onPointerDown,
}: {
  onPointerDown: (edge: ResizeEdge, e: RPointerEvent) => void;
}) {
  const common = {
    position: 'absolute' as const,
    zIndex: 10,
  };

  const handles: { edge: ResizeEdge; style: React.CSSProperties }[] = [
    { edge: 'n', style: { top: -HANDLE_SIZE / 2, left: HANDLE_SIZE, right: HANDLE_SIZE, height: HANDLE_SIZE } },
    { edge: 's', style: { bottom: -HANDLE_SIZE / 2, left: HANDLE_SIZE, right: HANDLE_SIZE, height: HANDLE_SIZE } },
    { edge: 'w', style: { left: -HANDLE_SIZE / 2, top: HANDLE_SIZE, bottom: HANDLE_SIZE, width: HANDLE_SIZE } },
    { edge: 'e', style: { right: -HANDLE_SIZE / 2, top: HANDLE_SIZE, bottom: HANDLE_SIZE, width: HANDLE_SIZE } },
    { edge: 'nw', style: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, width: HANDLE_SIZE * 2, height: HANDLE_SIZE * 2 } },
    { edge: 'ne', style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, width: HANDLE_SIZE * 2, height: HANDLE_SIZE * 2 } },
    { edge: 'sw', style: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, width: HANDLE_SIZE * 2, height: HANDLE_SIZE * 2 } },
    { edge: 'se', style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, width: HANDLE_SIZE * 2, height: HANDLE_SIZE * 2 } },
  ];

  return (
    <>
      {handles.map((h) => (
        <div
          key={h.edge}
          style={{ ...common, ...h.style, cursor: EDGE_CURSORS[h.edge] }}
          onPointerDown={(e) => {
            e.stopPropagation();
            onPointerDown(h.edge, e);
          }}
        />
      ))}
    </>
  );
}

export const DashboardCanvas = () => {
  const { draftLayout, draftWidgets, isEditing, removeWidget, updateLayoutItem } =
    useDashboard();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [zOrder, setZOrder] = useState<string[]>([]);
  const [liveRect, setLiveRect] = useState<DashboardLayoutItem | null>(null);

  const dragRef = useRef<DragState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);

  const bringToFront = useCallback((id: string) => {
    setZOrder((prev) => [...prev.filter((x) => x !== id), id]);
  }, []);

  const widgetMap = useMemo(() => {
    const map = new Map<string, (typeof draftWidgets)[number]>();
    for (const w of draftWidgets) map.set(w.id, w);
    return map;
  }, [draftWidgets]);

  const canvasHeight = useMemo(() => {
    const lowest = draftLayout.reduce(
      (max, item) => Math.max(max, item.y + item.height),
      0,
    );
    return lowest + CANVAS_PADDING + MIN_WIDGET_GAP;
  }, [draftLayout]);

  // ── Drag handlers ──

  const handleDragStart = useCallback(
    (id: string, e: RPointerEvent) => {
      if (!isEditing) return;
      const item = draftLayout.find((l) => l.i === id);
      if (!item) return;

      bringToFront(id);
      dragRef.current = {
        id,
        startX: e.clientX,
        startY: e.clientY,
        origX: item.x,
        origY: item.y,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [isEditing, draftLayout, bringToFront],
  );

  const handlePointerMove = useCallback(
    (e: RPointerEvent) => {
      if (dragRef.current) {
        const d = dragRef.current;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        const item = draftLayout.find((l) => l.i === d.id);
        if (!item) return;
        setLiveRect({
          ...item,
          x: snap(Math.max(0, d.origX + dx)),
          y: snap(Math.max(0, d.origY + dy)),
        });
        return;
      }

      if (resizeRef.current) {
        const r = resizeRef.current;
        const dx = e.clientX - r.startPointerX;
        const dy = e.clientY - r.startPointerY;
        let { x, y, width, height } = r.orig;

        if (r.edge.includes('e')) width = Math.max(r.minW, width + dx);
        if (r.edge.includes('w')) {
          const newW = Math.max(r.minW, width - dx);
          x = x + (width - newW);
          width = newW;
        }
        if (r.edge.includes('s')) height = Math.max(r.minH, height + dy);
        if (r.edge.includes('n')) {
          const newH = Math.max(r.minH, height - dy);
          y = y + (height - newH);
          height = newH;
        }

        const item = draftLayout.find((l) => l.i === r.id);
        if (!item) return;
        setLiveRect({
          ...item,
          x: snap(Math.max(0, x)),
          y: snap(Math.max(0, y)),
          width: snap(width),
          height: snap(height),
        });
      }
    },
    [draftLayout],
  );

  const handlePointerUp = useCallback(() => {
    if (liveRect) {
      const id = liveRect.i;
      updateLayoutItem(id, {
        x: liveRect.x,
        y: liveRect.y,
        width: liveRect.width,
        height: liveRect.height,
      });
      setLiveRect(null);
    }
    dragRef.current = null;
    resizeRef.current = null;
  }, [liveRect, updateLayoutItem]);

  // ── Resize handlers ──

  const handleResizeStart = useCallback(
    (id: string, edge: ResizeEdge, e: RPointerEvent) => {
      if (!isEditing) return;
      const item = draftLayout.find((l) => l.i === id);
      if (!item) return;
      const definition = getWidget(widgetMap.get(id)?.type ?? '');

      bringToFront(id);
      resizeRef.current = {
        id,
        edge,
        startPointerX: e.clientX,
        startPointerY: e.clientY,
        orig: { x: item.x, y: item.y, width: item.width, height: item.height },
        minW: definition?.minSize.width ?? DEFAULT_MIN_W,
        minH: definition?.minSize.height ?? DEFAULT_MIN_H,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [isEditing, draftLayout, widgetMap, bringToFront],
  );

  const gridBackground = isDark
    ? 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)'
    : 'radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)';

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100%',
        height: canvasHeight,
        backgroundImage: isEditing ? gridBackground : undefined,
        backgroundSize: isEditing ? `${SNAP_GRID}px ${SNAP_GRID}px` : undefined,
      }}
      onPointerMove={isEditing ? handlePointerMove : undefined}
      onPointerUp={isEditing ? handlePointerUp : undefined}
    >
      {draftLayout.map((layoutItem) => {
        const widgetConfig = widgetMap.get(layoutItem.i);
        if (!widgetConfig) return null;

        const active = liveRect?.i === layoutItem.i ? liveRect : layoutItem;
        const z = zOrder.indexOf(layoutItem.i);

        return (
          <div
            key={layoutItem.i}
            style={{
              position: 'absolute',
              left: active.x,
              top: active.y,
              width: active.width,
              height: active.height,
              zIndex: z >= 0 ? z + 1 : undefined,
            }}
            onPointerDown={() => bringToFront(layoutItem.i)}
          >
            <WidgetFrame
              widgetConfig={widgetConfig}
              layoutItem={layoutItem}
              isEditing={isEditing}
              onDelete={removeWidget}
              onDragStart={(e) => handleDragStart(layoutItem.i, e)}
            />
            {isEditing && (
              <ResizeHandles
                onPointerDown={(edge, e) => handleResizeStart(layoutItem.i, edge, e)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
